import React from "react";
import { useForm, router } from "@inertiajs/react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Eye, EyeOff, Info } from "lucide-react";

interface Props {
  errors?: {
    email_address?: string[];
    password?: string[];
    password_confirmation?: string[];
  };
  user?: {
    email_address?: string;
  };
  claim_physiotherapist_id?: string;
}

export default function New({ errors, user, claim_physiotherapist_id }: Props) {
  const { data, setData, post, processing } = useForm({
    user: {
      email_address: user?.email_address || "",
      password: "",
      password_confirmation: "",
    }
  });

  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = React.useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/registration");
  };

  return (
    <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Créer un compte
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Ou{" "}
          <a
            href={claim_physiotherapist_id ? `/session/new?claim_physiotherapist_id=${claim_physiotherapist_id}` : "/session/new"}
            className="font-medium text-green-600 hover:text-green-500"
            onClick={(e) => {
              e.preventDefault();
              const loginUrl = claim_physiotherapist_id
                ? `/session/new?claim_physiotherapist_id=${claim_physiotherapist_id}`
                : '/session/new';
              router.visit(loginUrl, {
                method: 'get',
                preserveScroll: false,
                preserveState: false,
                replace: true
              });
            }}
          >
            connectez-vous à votre compte existant
          </a>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {errors && Object.keys(errors).length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  {Object.values(errors).flat().map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            <div>
              <label htmlFor="email_address" className="block text-sm font-medium text-gray-700">
                Adresse email
              </label>
              <div className="mt-1">
                <Input
                  id="email_address"
                  name="user[email_address]"
                  type="email"
                  autoComplete="email"
                  required
                  value={data.user.email_address}
                  onChange={e => setData("user", { ...data.user, email_address: e.target.value })}
                  className={errors?.email_address ? "border-red-500" : ""}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="mt-1 relative">
                <Input
                  id="password"
                  name="user[password]"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={data.user.password}
                  onChange={e => setData("user", { ...data.user, password: e.target.value })}
                  className={`${errors?.password ? "border-red-500" : ""} pr-10`}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <Info size={14} />
                Le mot de passe doit contenir au moins 8 caractères.
              </p>
            </div>

            <div>
              <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700">
                Confirmer le mot de passe
              </label>
              <div className="mt-1 relative">
                <Input
                  id="password_confirmation"
                  name="user[password_confirmation]"
                  type={showPasswordConfirmation ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={data.user.password_confirmation}
                  onChange={e => setData("user", { ...data.user, password_confirmation: e.target.value })}
                  className={`${errors?.password_confirmation ? "border-red-500" : ""} pr-10`}
                />
                <button
                  type="button"
                  aria-label={showPasswordConfirmation ? "Masquer la confirmation" : "Afficher la confirmation"}
                  onClick={() => setShowPasswordConfirmation(p => !p)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  {showPasswordConfirmation ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                className="w-full"
                disabled={processing}
              >
                {processing ? "Création du compte..." : "Créer un compte"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 
